import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, IconButton, Paper, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PanelShell from '../../Map/components/PanelShell/PanelShell';
import PanelHeader, { panelCloseIconButtonSx } from '../../Map/components/PanelShell/PanelHeader';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { colors } from '../../styles/theme';
import InfoNotice from '../../Components/InfoNotice';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import { disconnectMeta, updateMetaAdAccounts } from '../../services/metaAdsService';
import {
  buildMetaChecklist, formatMetaCampaignsFreshness, formatMetaSyncTime, metaAdsOpensInCampaigns,
} from '../metaAdsConnection';
import { metaCampaignMix } from '../metaCampaigns';
import { formatMetaCountdown, getMetaErrorMessage, readMetaErrorCode } from '../metaAdsConfig';
import { describeSelectionChange } from '../metaAdAccounts';
import { useMetaConnect } from '../hooks/useMetaConnect';
import { useMetaAdAccounts } from '../hooks/useMetaAdAccounts';
import { useMetaCampaigns } from '../hooks/useMetaCampaigns';
import { META_DEFAULT_PERIOD_PRESET, metaPeriodPresetRange } from '../metaPeriod';
import { useMetaLinkActions } from '../hooks/useMetaLinkActions';
import MetaCampaignsView from './MetaCampaignsView';
import { MetaAnalysisView, MetaReportView } from './MetaSectionViews';
import { MetaBudgetSummaries } from './MetaBudgetContent';
import { useMetaFase1Support } from '../metaFase1Support';
import { META_DEFAULT_ANALYSIS_TAB, META_SUBVIEWS } from '../metaNavigation';
import MetaSubviewDialog from './MetaSubviewDialog';
import MetaSettingsButton from './MetaSettingsButton';
import MetaWriteSettingsRow from './MetaWriteSettingsRow';
import MetaWriteLogDialog from './MetaWriteLogDialog';
import MetaCreatePickRecordDialog from './MetaCreatePickRecordDialog';
import { metaCreateAccess } from '../metaCreate';
import { applyMetaWriteResult, metaWriteSuccessMessage, metaWriteVisible } from '../metaWrite';
import { offerMetaAutoUnshare } from '../metaShareGuard';
import { isDeletedMetaCampaign, rememberDeletedMetaCampaign } from '../metaDeletedCampaigns';
import { consumeMetaWriteReturn, peekMetaWriteReturn } from '../metaWriteReturn';
import { META_INITIAL_LOAD_MESSAGE, useMetaSync } from '../hooks/useMetaSync';
import MetaSyncBanner, { MetaSyncRefreshButton, metaSyncBannerModel } from './MetaSyncBanner';
import { useMetaAdsContext } from '../MetaAdsContext';
import MetaAdAccountsView from './MetaAdAccountsView';
import MetaAdsChecklist from './MetaAdsChecklist';
import MetaConnectStep, { MetaConnectButton } from './MetaConnectStep';
import { metaAdsBodyFixedSx, metaAdsTabPaneSx, metaAdsBodySx, metaAdsDisconnectRowSx, metaAdsHeaderIconActionSx, metaAdsHeroButtonSx, metaAdsMutedTextSx, metaAdsPanelPaperSx, metaAdsQuietButtonSx } from './metaAdsUi.styles';
import MetaAdsPanelTitle from './MetaAdsPanelTitle';

export const SUBVIEW_ICON_SX = { fontSize: '18px !important' };
const SUBVIEW_ICONS = {
  analysis: <BarChartOutlinedIcon sx={SUBVIEW_ICON_SX} />,
  report: <DescriptionOutlinedIcon sx={SUBVIEW_ICON_SX} />,
};
const META_ADS_PANEL_TITLE = 'CAMPAÑAS';

/** Estados con token guardado: ahí tiene sentido «Desconectar Meta» (solo dueño). */
const DISCONNECTABLE_STATUSES = ['active', 'expired', 'error'];
export const canDisconnectMeta = (connection) => Boolean(connection?.viewerCanManage
  && DISCONNECTABLE_STATUSES.includes(connection.status));
/** Subtítulo del panel: para qué sirve. El cómo (conectar) vive en el cuerpo. */
export const META_ADS_SUBTITLE = 'Qué rinde cada anuncio en tus unidades.';

function checklistSubtitle(connection, checklist) {
  if (!connection || !connection.connected) return META_ADS_SUBTITLE;
  if (checklist?.complete) return 'Resultados de Meta en tus unidades.';
  return 'Meta está conectado.';
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Texto de los pasos 2 y 3 (sus pantallas llegan en E2 / E3). */
function describeLaterStep(step, connection) {
  if (step.key === 'ad_account' && !connection.viewerCanManage && step.status !== 'done') {
    return 'El dueño del espacio tiene que elegir qué cuentas publicitarias seguir.';
  }
  if (step.key === 'ad_account') {
    if (step.status === 'done') {
      const n = connection.adAccounts.trackedCount;
      return n > 0
        ? `Seguís ${plural(n, 'cuenta publicitaria', 'cuentas publicitarias')}.`
        : 'Ya elegiste qué cuenta publicitaria seguir.';
    }
    if (step.status === 'none_available') {
      return 'No compartiste ninguna cuenta publicitaria. Volvé a conectar y marcala en la ventana de Meta.';
    }
    return 'Elegí qué cuentas publicitarias seguir. Mostramos el nombre y la moneda de cada una.';
  }
  if (step.status === 'done') return 'Todos los anuncios están vinculados con sus unidades.';
  if (step.status === 'no_ads') return 'No hay anuncios activos ni con gasto en los últimos 90 días.';
  if (step.status === 'partial') {
    const fresh = connection.ads.unlinkedNewCount;
    // `ads.unlinked_count` cuenta anuncios (activos), no campañas: se dice así.
    const base = `Hay campañas con ${plural(connection.ads.unlinkedCount, 'anuncio sin vincular', 'anuncios sin vincular')}.`;
    return fresh > 0 ? `${base} ${plural(fresh, 'es nuevo', 'son nuevos')} en campañas ya vinculadas.` : base;
  }
  return 'Vinculá cada anuncio con la unidad que promociona para ver sus resultados por unidad.';
}

/**
 * Avisos arriba del checklist: token por vencer y, solo si la API no manda
 * `sync_progress` (respuesta vieja), sync en curso / con error. Con
 * `sync_progress` todo eso lo dice MetaSyncBanner (Ajuste 7).
 */
function MetaAdsNotices({ connection, connecting, onConnect, ownSyncRunning = false, hasSyncProgress = false }) {
  const notices = [];
  if (ownSyncRunning || hasSyncProgress) {
    // El banner de la carga ya dice qué pasa.
  } else if (connection.syncRunning) {
    notices.push(
      <InfoNotice key="sync-running" title="Actualizando…" description="Estamos trayendo los datos de Meta. Podés seguir usando Terrio mientras tanto." data-testid="meta-ads-sync-running" />,
    );
  } else if (connection.lastSyncStatus === 'error' || connection.lastSyncStatus === 'partial') {
    const at = formatMetaSyncTime(connection.lastSyncAt);
    notices.push(
      <InfoNotice
        key="sync-error"
        title="La última actualización no se completó"
        description={at ? `Mostramos los datos hasta ${at}.` : 'Mostramos los últimos datos que pudimos traer.'}
        data-testid="meta-ads-sync-error"
      />,
    );
  }
  if (connection.tokenExpiringSoon && !connection.needsReconnect) {
    notices.push(
      <InfoNotice
        key="token-expiring"
        title="La conexión con Meta vence pronto"
        description={connection.viewerCanManage
          ? 'Reconectá para no cortar la actualización de los datos.'
          : 'Avisale al dueño del espacio para que la reconecte.'}
        actionLabel={connection.viewerCanManage ? 'Reconectar' : ''}
        onAction={connection.viewerCanManage ? onConnect : undefined}
        actionDisabled={connecting}
        data-testid="meta-ads-token-expiring"
      />,
    );
  }
  return notices.length ? notices : null;
}

/**
 * Avisos de la sync que disparó este panel: 429 con cuenta regresiva (solo si
 * la API no pidió seguir sola) y reconectar. La carga en curso / pendiente la
 * muestra MetaSyncBanner.
 */
function SyncNotices({ sync, canManage, connecting, onConnect }) {
  // La sync sola al abrir no avisa nada (429 / 409 respetan la espera en silencio).
  if (sync.running || sync.outcome?.silent) return null;
  const kind = sync.outcome?.kind;
  if (kind === 'throttled' && !sync.outcome.continuing) {
    const waiting = sync.retryInS > 0;
    return (
      <InfoNotice
        title={sync.outcome.message}
        description={waiting ? `Podés volver a intentarlo en ${formatMetaCountdown(sync.retryInS)}.` : 'Ya podés volver a intentarlo.'}
        actionLabel={canManage ? 'Traer datos' : ''}
        onAction={canManage ? () => sync.run({ initial: sync.initialLoad }) : undefined}
        actionDisabled={waiting}
        data-testid="meta-ads-sync-throttled"
      />
    );
  }
  if (kind === 'reconnect') {
    return (
      <InfoNotice
        title="Hay que volver a conectar Meta"
        description={sync.outcome.message}
        actionLabel={canManage ? 'Reconectar' : ''}
        onAction={canManage ? onConnect : undefined}
        actionDisabled={connecting}
        data-testid="meta-ads-sync-reconnect"
      />
    );
  }
  return null;
}

const textButtonSx = { textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 1, minWidth: 0 };

/**
 * API sin `sync_progress` pero con la carga de 90 días pendiente: el mismo
 * banner con un progreso armado acá (sin botón ni continuación sola).
 */
function legacySyncProgress(connection) {
  if (!connection?.connected || !connection.adAccounts?.initialLoadPending) return null;
  return {
    state: 'initial_load', message: META_INITIAL_LOAD_MESSAGE, detail: null, tone: 'info',
    showProgressBar: false, pct: null, syncRunning: connection.syncRunning, canSync: false, shouldContinue: false,
  };
}

/**
 * El `sync_progress` más nuevo entre el de `getmetaconnection` y el de la
 * última respuesta de `syncmetanow`: gana el último que cambió.
 */
function useLatestSyncProgress(connectionProgress, ownProgress) {
  const ref = useRef({ conn: undefined, own: undefined, value: null });
  const r = ref.current;
  if (connectionProgress !== r.conn) {
    r.conn = connectionProgress;
    r.value = connectionProgress ?? null;
  }
  if (ownProgress !== r.own) {
    r.own = ownProgress;
    if (ownProgress) r.value = ownProgress;
  }
  return r.value;
}

/**
 * Panel «Meta Ads» (E1–E2): checklist de puesta en marcha y conexión. El estado
 * viene de `useMetaConnection` (MapPage, para ocultar la entrada del menú con
 * 404); al abrir se refresca sin skeleton. Dueño (`viewer_can_manage`)
 * conecta / reconecta y elige cuentas (vista «Cuentas publicitarias»); el
 * editor ve lo mismo en solo lectura con «Avisale al dueño».
 * Ver docs/maps/meta-ads.md.
 *
 * @param {{ metaConnection: object, uiLeftInset?: number, onClose: () => void }} props
 */
export default function MetaAdsPanel({
  metaConnection, uiLeftInset, onClose, viewerUserId = null, initialView = 'checklist', focusCampaign = null,
}) {
  const { loaded, error, connection, refresh, applyConnectionData, ownerUserId } = metaConnection;
  const { connecting, connect } = useMetaConnect({ onConnected: applyConnectionData });
  const canManage = Boolean(connection?.viewerCanManage);
  // MapPage: abrir la ficha de una unidad vinculada (chip en Campañas). Sin provider, null.
  const metaAdsContext = useMetaAdsContext();
  const canLink = Boolean(connection?.viewerCanLink);
  /** `checklist` | `accounts` | `campaigns` (vista dentro del panel; se resetea al cerrar o cambiar de espacio). */
  // `initialView: 'campaigns'` = «Ver todas las campañas» desde la ficha (E4).
  const [view, setView] = useState(() => (initialView === 'campaigns' || focusCampaign?.campaignId ? 'campaigns' : 'checklist'));
  // Reunión 29/10: la campaña elegida en el buscador vive mientras el panel está abierto (al cerrarlo
  // se desmonta y se reinicia). `focusCampaign` (`{ campaignId, seq }`, MapPage): la ficha pide abrir
  // Campañas con una campaña elegida; cada pedido nuevo (otro `seq`) vuelve a elegir.
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [focusCampaignId, setFocusCampaignId] = useState(() => (focusCampaign?.campaignId ? String(focusCampaign.campaignId) : null));
  const focusSeqRef = useRef(focusCampaign?.seq);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Con Meta conectado y una cuenta seguida, el inicio es Campañas (sin flecha atrás);
  // la puesta en marcha queda para conectar o elegir cuenta. `view` sigue siendo
  // `checklist` = «el inicio», así volver de Cuentas vuelve a Campañas.
  const opensInCampaigns = metaAdsOpensInCampaigns(connection);
  const shownView = view === 'checklist' && opensInCampaigns ? 'campaigns' : view;
  // ⚙ abierto alguna vez: hacen falta los nombres de las cuentas seguidas.
  const [settingsOpened, setSettingsOpened] = useState(false);
  const accountsState = useMetaAdAccounts({ ownerUserId, enabled: view === 'accounts' || (settingsOpened && shownView === 'campaigns') });
  const [includeInactive, setIncludeInactive] = useState(false);
  // Ajuste 10: período del selector («Últimos 30 días» por defecto); vive mientras el panel está abierto.
  const [periodPreset, setPeriodPreset] = useState(META_DEFAULT_PERIOD_PRESET);
  const period = useMemo(() => metaPeriodPresetRange(periodPreset), [periodPreset]);
  const campaignsState = useMetaCampaigns({
    ownerUserId,
    enabled: shownView === 'campaigns',
    includeInactive,
    period,
  });
  const { reload: reloadCampaigns } = campaignsState;
  // Fase 1 (subvistas Análisis e Informe + detalle de campaña): solo si la API la soporta
  // (`metaFase1Support.js`); si no, el panel queda como antes. La lista de campañas sigue viva en
  // las subvistas (el hook no depende de la vista) y el período se comparte.
  const fase1 = useMetaFase1Support(campaignsState, ownerUserId);
  const [analysisTab, setAnalysisTab] = useState(META_DEFAULT_ANALYSIS_TAB);
  const [scopeCampaignId, setScopeCampaignId] = useState('');
  // Paso 2 del refactor: sin pestañas. Análisis e Informe se abren desde el encabezado de Campañas;
  // reunión 29/10: en un modal sobre el panel (`MetaSubviewDialog`), sin cambiar de sección.
  const [subview, setSubview] = useState(null);
  const shownSubview = fase1 && shownView === 'campaigns' ? subview : null;
  useEffect(() => {
    if (subview && shownView !== 'campaigns') setSubview(null);
  }, [subview, shownView]);
  useEffect(() => {
    if (!focusCampaign || focusCampaign.seq === focusSeqRef.current) return;
    focusSeqRef.current = focusCampaign.seq;
    if (!focusCampaign.campaignId) return;
    setFocusCampaignId(String(focusCampaign.campaignId));
    setSubview(null);
    setView('campaigns');
  }, [focusCampaign]);
  const handleFocusHandled = useCallback(() => setFocusCampaignId(null), []);
  // `campaignsVersion` (MapPage): sube cuando una ficha vincula / desvincula (o tras una acción en
  // Meta). El panel recarga Campañas en silencio, salvo las subidas que hizo él mismo (ya recargó).
  const invalidateCampaignsCtx = metaAdsContext?.invalidateCampaigns;
  const campaignsVersion = metaAdsContext?.campaignsVersion || 0;
  const ownVersionBumpsRef = useRef(0);
  const lastVersionRef = useRef(campaignsVersion);
  const notifyCampaignsChanged = useCallback(() => {
    if (!invalidateCampaignsCtx) return;
    ownVersionBumpsRef.current += 1;
    invalidateCampaignsCtx();
  }, [invalidateCampaignsCtx]);
  useEffect(() => {
    if (campaignsVersion === lastVersionRef.current) return;
    lastVersionRef.current = campaignsVersion;
    if (ownVersionBumpsRef.current > 0) {
      ownVersionBumpsRef.current -= 1;
      return;
    }
    if (shownView === 'campaigns') reloadCampaigns({ silent: true });
  }, [campaignsVersion, shownView, reloadCampaigns]);
  const handleLinksChanged = useCallback(() => {
    // Checklist y badge salen de getmetaconnection; la lista se recarga sin skeleton y las fichas
    // abiertas vuelven a pedir su sección «Campañas».
    refresh();
    reloadCampaigns({ silent: true });
    notifyCampaignsChanged();
  }, [refresh, reloadCampaigns, notifyCampaignsChanged]);
  const linkActions = useMetaLinkActions({ ownerUserId, campaignsState, onChanged: handleLinksChanged });
  // «Crear campaña» del encabezado (F2b API): solo el dueño en su espacio con `create_campaigns: available`.
  // Al crear / eliminar lo creado: la misma recarga que al vincular (conexión, lista y fichas).
  const canCreate = metaCreateAccess({ connection, ownerUserId });
  const [createOpen, setCreateOpen] = useState(false);

  // F2a · Acciones en Meta: solo con fase 1 y el bloque `write` de getmetaconnection (sin él, PROD igual).
  const connectionWrite = connection?.write || null;
  const writeVisible = fase1 && metaWriteVisible(connectionWrite);
  const [settingsOpenRequest, setSettingsOpenRequest] = useState(0);
  const openSettings = useCallback(() => setSettingsOpenRequest((n) => n + 1), []);
  const connectWrite = useCallback(() => connect({ access: 'write' }), [connect]);
  const { setCampaigns, campaigns: loadedCampaigns } = campaignsState;
  const invalidateUnitCampaigns = notifyCampaignsChanged;
  // F2b: sube con cada escritura → el detalle vuelve a pedir los conjuntos (su bloque `write`).
  const [writeVersion, setWriteVersion] = useState(0);
  const [writeLogOpen, setWriteLogOpen] = useState(false);
  const handleWriteApplied = useCallback((result, target) => {
    // Decisión 6: lo que dice la respuesta (estado, nombre, write, entrega en cascada) y después
    // la lista se recarga en silencio con el período del panel; las fichas abiertas también.
    setCampaigns((prev) => applyMetaWriteResult(prev, result, { includeInactive }));
    // Campaña eliminada desde acá: la API la sigue contando en el total; «Todas (N)» no.
    if (target.level === 'campaign' && (target.action === 'delete' || isDeletedMetaCampaign(result.campaign))) {
      rememberDeletedMetaCampaign(ownerUserId, result.campaign?.campaign_id ?? result.campaignId);
    }
    const message = metaWriteSuccessMessage(target.level, target.action, result);
    if (result.noChange) toast.info({ message });
    else toast.success({ message });
    if (!result.refreshed && result.refreshNote) toast.info({ message: result.refreshNote });
    // Paso 3 (t1099u): campaña de Terrio eliminada cuyo share lo creó «Crear campaña» y ya no lo usa
    // otra → el mismo toast ofrece «Dejar de compartir».
    if (target.level === 'campaign' && target.action === 'delete' && !result.noChange) {
      const deleted = (loadedCampaigns || []).find((c) => c.id === target.id);
      if (deleted?.createdInTerrio) {
        const records = (deleted.ads || []).flatMap((ad) => ad.linkedRecords || []);
        offerMetaAutoUnshare({ records, deletedCampaignId: target.id, baseMessage: message });
      }
    }
    reloadCampaigns({ silent: true });
    refresh();
    invalidateUnitCampaigns?.();
    setWriteVersion((n) => n + 1);
  }, [setCampaigns, loadedCampaigns, includeInactive, reloadCampaigns, refresh, invalidateUnitCampaigns, ownerUserId]);
  // Solo quien puede escribir (el dueño: `viewer_can_write`) recibe acciones; al editor / invitado no
  // le llega nada: sin ítems de Meta, y el ⋮ sin ítems no se renderiza.
  const viewerCanWrite = Boolean(connectionWrite?.viewerCanWrite);
  const writeActions = useMemo(() => (writeVisible && viewerCanWrite ? {
    isOwner: true,
    onApplied: handleWriteApplied,
    onOpenSettings: openSettings,
    onReconnect: connectWrite,
    onStale: () => { reloadCampaigns({ silent: true }); setWriteVersion((n) => n + 1); },
    version: writeVersion,
  } : null), [writeVisible, viewerCanWrite, handleWriteApplied, openSettings, connectWrite, reloadCampaigns, writeVersion]);
  // Vuelta del login «con escritura»: ⚙ abierto una vez (la marca la deja useMetaConnect).
  const writeReturnRef = useRef(null);
  if (writeReturnRef.current === null) writeReturnRef.current = peekMetaWriteReturn();

  const campaignsLoadedRef = useRef(false);
  campaignsLoadedRef.current = Boolean(campaignsState.loaded);
  const accountsLoadedRef = useRef(false);
  accountsLoadedRef.current = Boolean(accountsState.loaded);
  const { reload: reloadAccounts } = accountsState;
  const handleSyncSettled = useCallback((outcome) => {
    // Mientras la API pide seguir (sola) no hay toast: el banner muestra el avance.
    if (!outcome.continuing) {
      if (outcome.kind === 'ok' && !outcome.auto) toast.success({ message: 'Listo: ya trajimos los datos de Meta.' });
      else if (outcome.kind === 'in_progress' && !outcome.auto) toast.info({ message: outcome.message });
      else if (outcome.kind === 'partial' || outcome.kind === 'error' || outcome.kind === 'unknown') {
        toast.warning({ message: outcome.message });
      }
    }
    if (!mountedRef.current) return;
    // Después de cada vuelta: conexión (banner / checklist) y, si ya está cargada, la lista.
    refresh();
    if (campaignsLoadedRef.current) reloadCampaigns({ silent: true });
    // Sync ok: también las cuentas (puntaje de oportunidad y entrega), sin skeleton.
    if (outcome.kind === 'ok' && accountsLoadedRef.current) reloadAccounts({ silent: true });
  }, [refresh, reloadCampaigns, reloadAccounts]);
  const sync = useMetaSync({
    onSettled: handleSyncSettled,
    progress: connection?.syncProgress || null,
    // Solo el dueño con Meta activo sigue solo; desconectar / perder permiso corta la espera.
    autoContinue: canManage && Boolean(connection?.connected),
    // Ajuste 11: al volver el foco, conexión fresca → `should_auto_sync`.
    refreshConnection: refresh,
  });
  const latestProgress = useLatestSyncProgress(connection?.syncProgress || null, sync.progress);
  const bannerProgress = latestProgress || legacySyncProgress(connection);

  useEffect(() => {
    if (!writeReturnRef.current || shownView !== 'campaigns' || !connection?.connected) return;
    writeReturnRef.current = false;
    consumeMetaWriteReturn();
    openSettings();
  }, [shownView, connection, openSettings]);

  const openCampaigns = () => setView('campaigns');

  const openAccounts = () => {
    setSaveError(null);
    setView('accounts');
  };

  const handleSaveAccounts = async (ids) => {
    const { removed } = describeSelectionChange(ids, accountsState.accounts);
    if (ids.length === 0 && removed.length > 0) {
      const { isConfirmed } = await confirmDialog({
        title: '¿Dejar de seguir todas las cuentas?',
        description: 'Dejás de ver campañas y resultados de Meta hasta que elijas otra. No se borran datos ni vínculos.',
        confirmLabel: 'Dejar de seguir',
        variant: 'warning',
      });
      if (!isConfirmed) return;
    }
    setSaving(true);
    setSaveError(null);
    const res = await updateMetaAdAccounts(ids);
    if (!mountedRef.current) return;
    setSaving(false);
    if (!res?._httpOk) {
      setSaveError(res);
      toast.error({ title: 'No pudimos guardar las cuentas', message: getMetaErrorMessage(res) });
      if (readMetaErrorCode(res) === 'META_AD_ACCOUNT_NOT_FOUND') accountsState.reload();
      return;
    }
    accountsState.replaceAccounts(res);
    toast.success({
      message: ids.length
        ? `Listo: seguís ${plural(ids.length, 'cuenta publicitaria', 'cuentas publicitarias')}.`
        : 'Dejaste de seguir las cuentas publicitarias.',
    });
    setView('checklist');
    refresh();
    if (res.initial_load_pending === true) sync.run({ initial: true });
  };
  /**
   * «Desconectar Meta» (E6, sin purge): confirma, `POST /disconnectmeta` con body `{}` y refresca
   * la conexión (es la misma de MetaAdsContext: las fichas pasan a historial). Idempotente.
   */
  const handleDisconnect = async () => {
    if (disconnecting) return;
    const { isConfirmed } = await confirmDialog({
      title: '¿Desconectar Meta?',
      description: 'Se conserva el historial de resultados y los vínculos con tus unidades; dejamos de traer datos nuevos. Podés volver a conectar cuando quieras.',
      confirmLabel: 'Desconectar',
      variant: 'warning',
    });
    if (!isConfirmed || !mountedRef.current) return;
    setDisconnecting(true);
    const res = await disconnectMeta();
    if (!mountedRef.current) return;
    setDisconnecting(false);
    if (!res?._httpOk) {
      toast.error({ title: 'No pudimos desconectar Meta', message: getMetaErrorMessage(res) });
      return;
    }
    toast.success({
      message: res.data?.already_disconnected === true ? 'Meta ya estaba desconectado.' : 'Desconectamos Meta. El historial se conserva.',
    });
    setView('checklist');
    refresh();
  };

  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const checklist = useMemo(() => (connection ? buildMetaChecklist(connection) : null), [connection]);

  useEffect(() => {
    refresh();
    // Solo al abrir el panel: el hook ya recarga al cambiar de espacio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeButton = (
    <IconButton onClick={onClose} size="small" aria-label="Cerrar panel" sx={panelCloseIconButtonSx}>
      <CloseIcon />
    </IconButton>
  );

  const renderStepBody = (step) => {
    if (step.key === 'connect') {
      return <MetaConnectStep step={step} connection={connection} connecting={connecting} onConnect={connect} />;
    }
    if (!connection.connected) return null;
    const text = describeLaterStep(step, connection);
    let actions = null;
    if (step.key === 'link_units' && (connection.adAccounts.trackedCount > 0 || step.status !== 'pending')) {
      const cta = canLink && (step.status === 'pending' || step.status === 'partial') ? 'Vincular campañas' : 'Ver campañas';
      actions = cta === 'Vincular campañas'
        ? <Button variant="contained" disableElevation onClick={openCampaigns} sx={metaAdsHeroButtonSx}>{cta}</Button>
        : <Button size="small" onClick={openCampaigns} sx={textButtonSx}>{cta}</Button>;
    }
    if (step.key === 'ad_account') {
      if (canManage && step.status === 'pending') {
        actions = <Button variant="contained" disableElevation onClick={openAccounts} sx={metaAdsHeroButtonSx}>Elegir cuentas</Button>;
      } else if (canManage && step.status === 'none_available') {
        actions = (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <MetaConnectButton label="Volver a conectar" connecting={connecting} onConnect={connect} />
            <Button size="small" onClick={openAccounts} sx={textButtonSx}>Ver cuentas</Button>
          </Box>
        );
      } else if (step.status === 'done') {
        actions = (
          <Button size="small" onClick={openAccounts} sx={textButtonSx}>
            {canManage ? 'Cambiar cuentas' : 'Ver cuentas seguidas'}
          </Button>
        );
      }
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={metaAdsMutedTextSx}>{text}</Typography>
        {actions}
      </Box>
    );
  };

  const bannerPendingMessage = sync.initialLoad ? META_INITIAL_LOAD_MESSAGE : null;
  // Dueño y la API deja (`can_sync`): «Actualizar datos» / «Seguir cargando».
  const canRunSync = canManage && Boolean(bannerProgress?.canSync);
  const syncBanner = (extra = {}) => (connection?.connected ? (
    <MetaSyncBanner
      progress={bannerProgress}
      pending={sync.running}
      pendingMessage={bannerPendingMessage}
      paused={sync.paused}
      onResume={canRunSync ? sync.resume : null}
      // Ícono «Actualizar datos» junto a «Datos al día»: pedido a mano (reinicia cuentas).
      onRefresh={canRunSync ? sync.resume : null}
      resumeInS={sync.retryInS}
      {...extra}
    />
  ) : null);
  const syncNotices = (
    <>
      <SyncNotices sync={sync} canManage={canManage} connecting={connecting} onConnect={connect} />
      <MetaAdsNotices
        connection={connection}
        connecting={connecting}
        onConnect={connect}
        ownSyncRunning={sync.running}
        hasSyncProgress={Boolean(bannerProgress)}
      />
    </>
  );

  /**
   * Campañas como inicio: «Datos al día» va en el header fijo de la lista; carga / pausa
   * («Seguir cargando») / error y los avisos van al inicio de la lista. ↻ «Actualizar datos» y
   * ⚙ «Configuración de Meta» van en el header del panel, a la izquierda de la ✕ (como los
   * íconos del header de Registros), en Campañas y en sus subvistas (no en Cuentas).
   */
  const campaignsChrome = () => {
    const model = connection?.connected
      ? metaSyncBannerModel(bannerProgress, { pending: sync.running, pendingMessage: bannerPendingMessage, paused: sync.paused })
      : null;
    const upToDate = model?.kind === 'done';
    // Mismo formato que `data_freshness_text` de la lista (t991u): no cambia la frase al llegar.
    // Sin «· Actualizando…»: el ↻ del header ya gira mientras refresca.
    const fallbackFreshness = upToDate
      ? formatMetaCampaignsFreshness({ detail: model.detail, lastSyncAt: bannerProgress?.lastSyncAt })
      : '';
    const trackedAccounts = accountsState.loaded
      ? (accountsState.accounts || []).filter((a) => a.isTracked)
      : null;
    const panelTools = (
      <>
        {upToDate && canRunSync ? (
          <MetaSyncRefreshButton refreshing={model.refreshing} onRefresh={sync.resume} resumeInS={sync.retryInS} inPanelHeader />
        ) : null}
        <MetaSettingsButton
          connection={connection}
          trackedAccounts={trackedAccounts}
          accountsLoading={!accountsState.loaded}
          campaignMix={metaCampaignMix(campaignsState, { includeInactive })}
          canManage={canManage}
          onOpen={() => setSettingsOpened(true)}
          onOpenAccounts={openAccounts}
          onDisconnect={canDisconnectMeta(connection) ? handleDisconnect : null}
          disconnecting={disconnecting}
          inPanelHeader
          openRequest={settingsOpenRequest}
          writeRow={writeVisible ? (
            <MetaWriteSettingsRow
              connectionWrite={connectionWrite}
              ownerUserId={ownerUserId}
              canManage={canManage}
              connecting={connecting}
              onReconnectWrite={connectWrite}
              onChanged={() => { refresh(); reloadCampaigns({ silent: true }); }}
            />
          ) : null}
          onOpenLog={writeVisible && viewerCanWrite ? () => setWriteLogOpen(true) : null}
        />
      </>
    );
    return { fallbackFreshness, panelTools };
  };

  const campaignsListNotices = (
    <>
      {syncBanner({ hideUpToDate: true })}
      {syncNotices}
    </>
  );

  const renderCampaigns = () => {
    const chrome = campaignsChrome();
    const { fallbackFreshness } = chrome;
    const createButton = canCreate ? (
      <Tooltip title={<span>Crear campaña</span>} placement="bottom" arrow>
        <IconButton
          size="small"
          aria-label="Crear campaña"
          onClick={() => setCreateOpen(true)}
          sx={metaAdsHeaderIconActionSx}
          data-testid="meta-campaigns-create"
        >
          <AddCircleOutlineIcon sx={SUBVIEW_ICON_SX} />
        </IconButton>
      </Tooltip>
    ) : null;
    const headerTools = fase1 || createButton ? (
      <>
        {fase1 ? META_SUBVIEWS.map((sv) => (
          // Solo ícono (29/10): el nombre va en el Tooltip y en el aria-label.
          <Tooltip key={sv.key} title={<span>{sv.actionLabel}</span>} placement="bottom" arrow>
            <IconButton
              size="small"
              aria-label={sv.actionLabel}
              onClick={() => setSubview(sv.key)}
              sx={metaAdsHeaderIconActionSx}
              data-testid={`meta-campaigns-open-${sv.key}`}
            >
              {SUBVIEW_ICONS[sv.key]}
            </IconButton>
          </Tooltip>
        )) : null}
        {createButton}
      </>
    ) : null;
    return (
      <MetaCampaignsView
        canLink={canLink}
        canManage={canManage}
        ownerUserId={ownerUserId}
        viewerUserId={viewerUserId}
        campaignsState={campaignsState}
        linkActions={linkActions}
        includeInactive={includeInactive}
        periodPreset={periodPreset}
        onPeriodPresetChange={setPeriodPreset}
        lastSyncAt={latestProgress?.lastSyncAt || connection?.lastSyncAt || null}
        onIncludeInactiveChange={setIncludeInactive}
        connecting={connecting}
        onReconnect={connect}
        onOpenRecord={metaAdsContext?.openRecordTarget || null}
        headerTools={headerTools}
        fallbackFreshness={fallbackFreshness}
        listNotices={campaignsListNotices}
        campaignDetail={fase1 ? { period, periodPreset, ownerUserId, includeInactive } : null}
        writeActions={writeActions}
        selectedCampaignId={selectedCampaignId}
        onSelectCampaign={setSelectedCampaignId}
        focusCampaignId={focusCampaignId}
        onFocusHandled={handleFocusHandled}
      />
    );
  };

  // Análisis e Informe (modal desde Campañas): encabezado fijo común (frescura, período). Los avisos
  // de carga quedan en el panel, detrás (un solo aviso).
  const subviewCommon = () => {
    const { fallbackFreshness } = campaignsChrome();
    return {
      freshness: campaignsState.dataFreshnessText || fallbackFreshness,
      periodPreset,
      onPeriodPresetChange: setPeriodPreset,
    };
  };

  // Análisis: campaña (Todas o una) → período → dimensión; mismos estados que antes.
  const renderAnalysis = () => (
    <MetaAnalysisView
      {...subviewCommon()}
      campaigns={(campaignsState.campaigns || []).map((c) => ({ id: c.id, name: c.name }))}
      scopeCampaignId={scopeCampaignId}
      onScopeChange={setScopeCampaignId}
      analysisTab={analysisTab}
      onAnalysisTabChange={setAnalysisTab}
      period={period}
      ownerUserId={ownerUserId}
      includeInactive={includeInactive}
    />
  );

  // Informe: mismos pedidos que antes + el resumen de presupuesto por moneda (ex vista Presupuesto),
  // armado con las campañas ya cargadas de getmetacampaigns (sin pedidos nuevos).
  const renderReport = () => (
    <MetaReportView
      {...subviewCommon()}
      period={period}
      ownerUserId={ownerUserId}
      includeInactive={includeInactive}
      afterSummary={campaignsState.loaded ? (
        <MetaBudgetSummaries campaigns={campaignsState.campaigns} filterLabel={includeInactive ? 'Todas' : 'Activas'} />
      ) : null}
    />
  );

  const renderBody = () => {
    // Sin conexión todavía no sabemos si el inicio es Campañas o la puesta en marcha: un loader
    // chico y neutro (sin tarjetas grises que no se parecen a lo que viene ni pasos que desaparecen).
    if (!loaded) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }} data-testid="meta-ads-loading" aria-busy="true">
          <CircularProgress size={20} thickness={5} sx={{ color: colors.primary }} aria-label="Cargando" />
        </Box>
      );
    }
    if (!connection) {
      return (
        <PanelEmptyState
          icon={CampaignOutlinedIcon}
          title="No pudimos cargar los anuncios"
          hint={error || 'Probá de nuevo en un rato.'}
          actionLabel="Reintentar"
          actionIcon={RefreshIcon}
          onAction={refresh}
          testId="meta-ads-load-error"
        />
      );
    }
    if (shownView === 'campaigns') {
      if (!fase1) return renderCampaigns();
      // Análisis e Informe van en un modal encima (se montan solo cuando se abren); Campañas sigue a la vista.
      return (
        <>
          <Box sx={metaAdsTabPaneSx} data-testid="meta-campaigns-pane">
            {renderCampaigns()}
          </Box>
          {shownSubview ? (
            <MetaSubviewDialog subview={shownSubview} analysisTab={analysisTab} onClose={() => setSubview(null)}>
              {shownSubview === 'analysis' ? renderAnalysis() : renderReport()}
            </MetaSubviewDialog>
          ) : null}
        </>
      );
    }
    if (view === 'accounts') {
      return (
        <MetaAdAccountsView
          canManage={canManage}
          accountsState={accountsState}
          saving={saving}
          saveError={saveError}
          onSave={handleSaveAccounts}
          connecting={connecting}
          onReconnect={connect}
        />
      );
    }
    return (
      <>
        {syncBanner()}
        {syncNotices}
        <MetaAdsChecklist checklist={checklist} renderStepBody={renderStepBody} />
        {canDisconnectMeta(connection) ? (
          <Box sx={metaAdsDisconnectRowSx}>
            <Button
              size="small"
              onClick={handleDisconnect}
              disabled={disconnecting}
              data-testid="meta-ads-disconnect"
              sx={metaAdsQuietButtonSx}
            >
              {disconnecting ? 'Desconectando…' : 'Desconectar Meta'}
            </Button>
          </Box>
        ) : null}
      </>
    );
  };

  const inAccounts = shownView === 'accounts' && Boolean(connection);
  const inCampaigns = shownView === 'campaigns' && Boolean(connection);
  // El título es «CAMPAÑAS»; Cuentas suma « / CUENTAS PUBLICITARIAS» (Análisis e Informe son modales).
  const titleCurrent = inAccounts ? 'CUENTAS PUBLICITARIAS' : null;
  // Campañas como inicio no tiene «atrás»; abierta desde la puesta en marcha / la ficha, sí.
  const backButton = inAccounts || (inCampaigns && !opensInCampaigns) ? (
    <IconButton
      onClick={() => setView('checklist')}
      size="small"
      aria-label="Volver a campañas"
      sx={{ color: '#E8F4F4', p: 0.5, mr: 0.25, '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' } }}
    >
      <ArrowBackIcon sx={{ fontSize: 20 }} />
    </IconButton>
  ) : null;

  return (
    <PanelShell uiLeftInset={uiLeftInset} onClose={onClose}>
      {({ snap }) => (
        <Paper elevation={0} sx={metaAdsPanelPaperSx} data-testid="meta-ads-panel">
          <PanelHeader
            title={(
              <MetaAdsPanelTitle
                root={META_ADS_PANEL_TITLE}
                current={titleCurrent}
              />
            )}
            subtitle={inCampaigns
              ? (canLink ? 'Vinculá cada anuncio con la unidad que promociona.' : 'Campañas y anuncios de las cuentas seguidas.')
              : inAccounts
                ? (canManage ? 'Elegí de qué cuentas traemos campañas y resultados.' : 'Las cuentas que sigue el dueño del espacio.')
                : checklistSubtitle(connection, checklist)}
            startAdornment={backButton}
            actions={inCampaigns ? (
              <>
                {campaignsChrome().panelTools}
                {closeButton}
              </>
            ) : closeButton}
          />
          {snap !== 'collapsed' ? (
            <Box
              // Campañas maneja su scroll (header fijo + lista); el cuerpo no scrollea (sin doble scroll).
              ref={inCampaigns ? undefined : scrollRef}
              sx={inCampaigns ? metaAdsBodyFixedSx : { ...metaAdsBodySx, ...getScrollEdgeFadeSx(fadeTop, fadeBottom) }}
              data-testid="meta-ads-body"
              data-scroll={inCampaigns ? 'view' : 'body'}
              aria-busy={!loaded || undefined}
            >
              {renderBody()}
            </Box>
          ) : null}
          {writeVisible && viewerCanWrite && writeLogOpen ? (
            <MetaWriteLogDialog
              open
              onClose={() => setWriteLogOpen(false)}
              campaigns={(campaignsState.campaigns || []).map((c) => ({ id: c.id, name: c.name }))}
            />
          ) : null}
          {canCreate && createOpen ? (
            <MetaCreatePickRecordDialog open onClose={() => setCreateOpen(false)} onChanged={handleLinksChanged} />
          ) : null}
        </Paper>
      )}
    </PanelShell>
  );
}
